import { type SessionV2 } from "@decentriq/core";
import { type data_room as ddcDataRoom } from "ddc";
import { type PublishedDatasetsHashes } from "features/mediaDataRoom/models";
import { type MediaDataRoomJobResultTransform } from "../models";

export type V3MediaComputeRequest = Required<
  Omit<ddcDataRoom.action.DataRoomComputeAction, "kind">
>;
export type V3MediaComputeResponse = ddcDataRoom.action.DataRoomComputeAction;

export type MediaDataRoomComputeRequestKey = Exclude<
  keyof ddcDataRoom.action.DataRoomComputeAction,
  "kind"
>;

export type V3MediaComputeRequestValueOfKey<
  K extends MediaDataRoomComputeRequestKey,
> = V3MediaComputeRequest extends Record<K, infer V> ? V : never;

interface JobInputOptions<K extends MediaDataRoomComputeRequestKey> {
  key: K;
  dataRoomId: string;
  publishedDatasetsHashes: PublishedDatasetsHashes | null;
  timeout: number;
  skipCheckForExistingJob: boolean;
  version: string;
  dcrType: string;
  resourceId: string | null;
  subKey: string | null;
  jobId: string | null;
}

export class MediaDataRoomJobInput<K extends MediaDataRoomComputeRequestKey> {
  static emptyRequestCreator = () => ({});

  private constructor(private readonly options: JobInputOptions<K>) {}

  /**
   * This method is the only way to create a new instance of the `MediaDataRoomJobInput`
   * @param key `V3MediaComputeRequest` key; name of the request to be sent
   * @param dataRoomId data room ID
   * @param publishedDatasetsHashes instance of the `PublishedDatasetsHashes` or `null` if not needed. Hashes from this class are used to create the query & job keys
   * @returns new instance of the `MediaDataRoomJobInput`
   */
  public static create<K extends MediaDataRoomComputeRequestKey>(
    key: K,
    dataRoomId: string,
    publishedDatasetsHashes: PublishedDatasetsHashes | null = null
  ): MediaDataRoomJobInput<K> {
    return new MediaDataRoomJobInput<K>({
      dataRoomId,
      dcrType: "v3MediaDataRoom",
      jobId: null,
      key,
      publishedDatasetsHashes,
      resourceId: null,
      skipCheckForExistingJob: false,
      subKey: null,
      timeout: 3000,
      version: "v0",
    });
  }

  /**
   * This method is used to build a query prefix which only includes version, data room ID and driver attestation hash and type of the media data room
   * @param dataRoomId data room ID
   * @param driverAttestationHash driver attestation hash
   * @returns array of strings which are used as a prefix for the query key
   */
  public static buildQueryPrefix({
    dataRoomId,
    driverAttestationHash,
  }: {
    dataRoomId: string;
    driverAttestationHash: string;
  }): string[] {
    return [
      "v0",
      "v3MediaDataRoom",
      dataRoomId,
      "driverAttestationHash",
      driverAttestationHash,
    ];
  }

  /**
   * Returns `key` for this input
   */
  get key() {
    return this.options.key;
  }

  /**
   * Returns `dataRoomId` for this input
   */
  get dataRoomId() {
    return this.options.dataRoomId;
  }

  /**
   * As rest of the required fields for caching are passed during the creation of the input,
   * only the hash of the published datasets is needed to be checked whether or not query key got changed
   */
  get queryChangingKey() {
    return this.options.publishedDatasetsHashes?.datasetsHash ?? null;
  }

  /**
   * Returns `timeout` for this input
   */
  get timeout() {
    return this.options.timeout;
  }

  /**
   * Returns `skipCheckForExistingJob` for this input
   */
  get skipCheckForExistingJob() {
    return this.options.skipCheckForExistingJob;
  }

  /**
   * This method is used to construct an input with a new resource ID
   * @param resourceId an unique identifier of the entity for which this input is created
   * @returns new instance of the `MediaDataRoomJobInput` with passed resource ID
   */
  public withResourceId(resourceId: string): MediaDataRoomJobInput<K> {
    return this.cloneWith({ resourceId });
  }

  /**
   * This method is used to construct an input with a new sub key
   * @param subKey an additional identifier to the `key` provided during creation of the input
   * and adds extra layer splitting under same `key`
   * @returns new instance of the `MediaDataRoomJobInput` with passed sub key
   */
  public withSubKey(subKey: string): MediaDataRoomJobInput<K> {
    return this.cloneWith({ subKey });
  }

  /**
   * This method is used to construct an input with a job status sub key
   * @returns new instance of the `MediaDataRoomJobInput` with `jobStatus` sub key
   */
  public withJobStatusSubKey(): MediaDataRoomJobInput<K> {
    return this.withSubKey("jobStatus");
  }

  /**
   * This method is used to construct an input with an existing job sub key
   * @returns new instance of the `MediaDataRoomJobInput` with `existingJob` sub key
   */
  public withExistingJobSubKey(): MediaDataRoomJobInput<K> {
    return this.withSubKey("existingJob");
  }

  /**
   * This method is used to construct an input with an fetch results sub key
   * @returns new instance of the `MediaDataRoomJobInput` with `fetchResults` sub key
   */
  public withFetchResultsSubKey(): MediaDataRoomJobInput<K> {
    return this.withSubKey("fetchResults");
  }

  /**
   * This method is used to construct an input with a new job identifiers which helps to attach input to specific job
   * @param job `jobIdHex` and `computeNodeName` which are used to identify the job
   * @returns new instance of the `MediaDataRoomJobInput` with passed job
   */
  public withJob(
    job: { id: string } | null | undefined
  ): MediaDataRoomJobInput<K> {
    return this.cloneWith({
      jobId: job?.id ?? null,
    });
  }

  /**
   * This method is used to construct an input with a new timeout for the job result fetching
   * @param timeout timeout in milliseconds
   * @returns new instance of the `MediaDataRoomJobInput` with passed timeout
   */
  public withTimeout(timeout: number): MediaDataRoomJobInput<K> {
    return this.cloneWith({
      timeout,
    });
  }

  /**
   * This method is used to construct an input for which always fresh job will be created
   * @returns new instance of the `MediaDataRoomJobInput` without check for existing job
   */
  public withFreshJob(): MediaDataRoomJobInput<K> {
    return this.cloneWith({
      skipCheckForExistingJob: true,
    });
  }

  /**
   * This method builds a query key using identifiers attached to this input, only identifier which is not included in the query key is `audiencesHash`
   * which is not important of the query key and is used only for job key and cache identification in the remote cache storage
   * @returns array of strings or `null` values
   */
  public buildQueryKey(): (string | null)[] {
    return [
      this.options.version,
      this.options.dcrType,
      this.options.dataRoomId,
      ...(this.options.publishedDatasetsHashes?.datasetsHash
        ? [
            "publishedDatasetsHash",
            this.options.publishedDatasetsHashes?.datasetsHash,
          ]
        : []),
      this.options.key as string | null,
      ...(this.options.resourceId ? [this.options.resourceId] : []),
      ...(this.options.jobId ? ["jobId", this.options.jobId] : []),
      ...(this.options.subKey ? [this.options.subKey] : []),
    ];
  }

  private cloneWith(
    updates: Partial<JobInputOptions<K>>
  ): MediaDataRoomJobInput<K> {
    return new MediaDataRoomJobInput<K>({
      ...this.options,
      ...updates,
    });
  }
}

export interface MediaDataRoomJobHookPayload<
  T,
  K extends MediaDataRoomComputeRequestKey,
> {
  input: MediaDataRoomJobInput<K>;
  /** @param requestCreator function which needs to return body of the request for the given key */
  requestCreator: () => V3MediaComputeRequestValueOfKey<K>;
  /** @param session optional `SessionV2`, if not provided, it will be fetched */
  session?: SessionV2;
  /** @param skip if true, the job result fetching will be skipped */
  skip: boolean;
  /** @param transform function to transform the job result, it provides the zip file in `JSZip` format */
  transform: MediaDataRoomJobResultTransform<T>;
}

export type V3MediaStateRequest = Required<
  Omit<ddcDataRoom.action.DataRoomAction, "kind">
>;
export type V3MediaStateResponse = Required<
  Omit<ddcDataRoom.action.DataRoomActionResponse, "kind">
>;

export type MediaDataRoomStateRequestKey = Exclude<
  keyof ddcDataRoom.action.DataRoomAction,
  "kind"
>;

export type V3MediaStateRequestValueOfKey<
  K extends MediaDataRoomStateRequestKey,
> = V3MediaStateRequest extends Record<K, infer V> ? V : never;

export type V3MediaStateResponseValueOfKey<
  K extends MediaDataRoomStateRequestKey,
> = V3MediaStateResponse extends Record<K, infer V> ? V : never;
